import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { sessionDate } from '@/lib/session-labels';
import type { SessionRow } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * « Supprimer une session », au patron des trois boîtes de §4 (§12.5).
 *
 * ## Elle DIT la conséquence, ce qui est toute sa raison d'être
 *
 * §12.5 : « une boîte au patron des trois boîtes de §4 (le focus sur "Annuler")
 * qui dit la conséquence. Corbeille de trente jours, restauration depuis la
 * liste ». Et la conséquence n'est pas « la ligne disparaît » : la purge emporte
 * les tours, les variables **et les entrées servies** (§8.7), qui portent le
 * verbatim une seconde fois. C'est aussi la réponse à une demande d'effacement,
 * puisque les variables d'identité vivent dans la session - donc une boîte qui
 * dirait seulement « supprimer ? » cacherait précisément ce pour quoi on clique.
 *
 * `VersionedDialog` est réutilisé et non recopié : c'est lui qui pose le voile,
 * l'Échap, et le focus sur **Annuler** à l'ouverture - la propriété qui compte
 * ici, parce qu'un Entrée égaré ne doit pas jeter une session.
 *
 * ## Les DEUX écrans montent ce composant-ci
 *
 * La liste (§12.1) et le parcours (§12.2) ouvrent la même boîte : c'est le même
 * geste sur la même session, et le parcours en a longtemps porté une copie qui
 * divergeait sur quatre points - le titre, le libellé du bouton, un ton `destructive`
 * que ce fichier refuse juste en dessous, et une phrase de purge qui oubliait
 * les variables. Une boîte qui dit la conséquence ne peut pas en dire deux.
 *
 * ## Le ton n'est pas `destructive`
 *
 * Le geste est réversible pendant trente jours, et la liste porte le bouton
 * « Restaurer » qui le défait. Un bouton rouge dirait « c'est fini » d'un geste
 * qui ne l'est pas ; le rouge reste pour ce qui ne revient pas.
 */

/**
 * Les trente jours de §12.5, épelés ici.
 *
 * Le serveur les tient dans `session/trash.ts` (`SESSION_TRASH_DAYS`), qui
 * n'est pas partagé : cette valeur-ci est donc une COPIE, et elle est écrite en
 * un seul endroit du frontend pour que la phrase de la boîte et la date de la
 * ligne en corbeille ne puissent pas se contredire.
 */
export const SESSION_TRASH_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Le jour où une session jetée maintenant serait effacée pour de bon. */
export function sessionPurgeDate(deletedAt: string, now = new Date()): string {
  const jettisoned = new Date(deletedAt);
  const from = Number.isNaN(jettisoned.getTime()) ? now : jettisoned;
  return sessionDate(new Date(from.getTime() + SESSION_TRASH_DAYS * DAY_MS).toISOString());
}

export interface SessionDeleteDialogProps {
  readonly row: SessionRow;
  readonly busy: boolean;
  /** Le refus, mis en mots par l'appelant depuis le `code`. Null quand il n'y en a pas. */
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function SessionDeleteDialog({
  row,
  busy,
  error,
  onConfirm,
  onCancel,
}: SessionDeleteDialogProps) {
  const name = row.experienceName ?? 'Expérience retirée';
  return (
    <VersionedDialog
      data-testid={SESSIONS_TEST_IDS.deleteDialog}
      title="Supprimer cette session ?"
      description={`« ${name} », commencée le ${sessionDate(row.startedAt)}.`}
      confirmLabel="Supprimer la session"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection
        title="Ce qui se passe"
        hint="« Afficher la corbeille » la retrouve, et « Restaurer » la remet dans la liste."
      >
        <p className="text-gray-700 text-small">
          Elle part à la corbeille pour <strong>{SESSION_TRASH_DAYS} jours</strong>, et y reste
          ouvrable en lecture sous un bandeau qui donne sa date d’effacement.
        </p>
      </VersionedDialogSection>

      <Alert tone="warning" title={`Passé les ${SESSION_TRASH_DAYS} jours, tout est effacé`}>
        Les tours de la conversation, les variables saisies et les entrées servies aux deux sièges
        disparaissent avec elle. Les entrées servies portent le verbatim une seconde fois : c’est
        pour cela que la purge les emporte aussi, et c’est ce qui fait de cette boîte la réponse à
        une demande d’effacement.
      </Alert>
    </VersionedDialog>
  );
}
